import { Component, OnInit, HostBinding, Output, EventEmitter, Input, ViewChild, TemplateRef, Injector, ComponentFactoryResolver, Optional, ChangeDetectorRef, Inject, LOCALE_ID } from '@angular/core';
import { DatagridComponent } from '@farris/ui-datagrid';
import { MessagerService } from '@farris/ui-messager';
import { TypeConverter } from '@farris/ide-property-panel';
import { BsModalService } from '@farris/ui-modal';
import { ProcessVariablesComponent } from '../process-variables/process-variables.component';
import { ProcessDesignerUIState } from '../../app/process-designer.uistate';
import { VariableSetting } from './variable-setting';
import { EditorTypes } from '@farris/ui-datagrid-editors';
import { ExprEditMode, ExpressionService, InjectedType } from '@gsp-svc/expression';
import { ComponentEditComponent } from './views/component-edit/component-edit.component';
import { RowEditParam } from '@farris/ui-datagrid/lib/types';
import { of } from 'rxjs';
import { WF_LANG_RESOURCES, WfLocalePipe } from '../../i18n/pipe/wf-process-editor-locale.pipe';
import { WF_PROCESS_EDITOR_LANG_RESOURCES } from '../../i18n/assets/lang.resource';

@Component({
  selector: 'lib-variable-setting',
  templateUrl: './variable-setting.component.html',
  styleUrls: ['./variable-setting.component.scss'],
  providers: [
     WfLocalePipe,
  ]
})
export class VariableSettingComponent implements OnInit {
  @HostBinding('class')
  cls = 'farris-main-area flex-column';
  @Output() closeModal = new EventEmitter<any>();
  @Output() submitModal = new EventEmitter<any>();

  @Input() value: Array<VariableSetting> = new Array();
  @Input() editorParams: any = {};
  @ViewChild('iconSelectFooter') modalFooter: TemplateRef<any>;
  @ViewChild('grid') grid: DatagridComponent;
  @ViewChild('cell') cell: TemplateRef<any>;

  columns = [];

  private tags = [
    { value: 'constant', text: this.localePipe.transform("component.variableSetting.constant") },
    { value: 'expression', text: this.localePipe.transform("component.variableSetting.expression") },
    { value: 'component', text: this.localePipe.transform("component.variableSetting.component") }
  ];

  private opportunities = [
    { value: 'ACTIVITY_STARTED', text: this.localePipe.transform("component.variableSetting.ACTIVITY_STARTED") },
    { value: 'ACTIVITY_COMPLETED', text: this.localePipe.transform("component.variableSetting.ACTIVITY_COMPLETED") }
  ];

  modalConfig = {
    title: this.localePipe.transform("component.variableSetting.varAssign"),
    width: 780,
    height: 460,
    showButtons: true
  };

  constructor(
    private resolver: ComponentFactoryResolver,
    private injector: Injector,
    private modalService: BsModalService,
    private message: MessagerService,
    private uistate: ProcessDesignerUIState,
    private expr: ExpressionService,
    @Optional() private cdr: ChangeDetectorRef,
    @Inject(LOCALE_ID) private localeId: string,
    @Inject(WF_LANG_RESOURCES) private resources: any,
    @Optional() private localePipe: WfLocalePipe,
  ) {
    this.localePipe = this.localePipe || new WfLocalePipe(localeId, resources);
  }

  ngOnInit() {
    this.columns = [
      {
        field: 'variableCode', width: 90, title: this.localePipe.transform("component.variableSetting.varNumber"), sortable: true,
        validators: [
          { type: 'required', messager: '必填' }
        ]
      },
      {
        field: 'variableName', width: 90, title: this.localePipe.transform("component.variableSetting.varName"), sortable: true,
        validators: [
          { type: 'required', messager: '必填' }
        ]
      }, {
        field: 'tag', width: 60, title: this.localePipe.transform("component.variableSetting.valueType"), sortable: true,
        formatter: {
          type: 'enum',
          options: { valueField: 'value', textField: 'text', data: this.tags }
        },
        editor: {
          type: EditorTypes.COMBOLIST,
          options: {
            valueField: 'value', textField: 'text', data: this.tags, idField: 'value', panelHeight: 'auto', showClear: false,
            selectChange: (e) => {
              this.value = this.value.map(v => {
                if (v.variableCode === e.rowId) {
                  v.value = null;
                  v.valueText = null;
                  if (v.component) {
                    v.component = null;
                  }
                  return v;
                } else {
                  return v;
                }
              });
            }
          }
        },
        validators: [
          { type: 'required', messager: '必填' }
        ]
      }, {
        field: 'executionTime', width: 80, title: this.localePipe.transform("component.variableSetting.executionTime"), sortable: true,
        formatter: {
          type: 'enum',
          options: { valueField: 'value', textField: 'text', data: this.opportunities }
        },
        editor: {
          type: EditorTypes.COMBOLIST,
          options: {
            valueField: 'value', textField: 'text', data: this.opportunities, idField: 'value', panelHeight: 'auto', showClear: false,
          }
        },
        validators: [
          { type: 'required', messager: '必填' }
        ],
      },
      {
        field: 'valueText', width: 150, title: this.localePipe.transform("component.variableSetting.valueText"), showTips: true,
        editor: { type: EditorTypes.TEXTBOX, options: {} },
        validators: [
          { type: 'required', messager: '必填' }
        ]
      }
    ];
  }

  add() {
    let dialog: any;
    const compFactory = this.resolver.resolveComponentFactory(ProcessVariablesComponent);
    const compRef = compFactory.create(this.injector);
    compRef.instance.variableSetting = true;
    compRef.instance.value = this.uistate.ProcessVariables;
    compRef.instance.selectedValue = this.value;
    compRef.instance.variableSetEmitter.subscribe((re: any[]) => {
      for (const v of re) {
        const variableSet = new VariableSetting();
        variableSet.variableCode = v.data.name;
        variableSet.variableName = v.data.documentation;
        variableSet.tag = v.data.tag || this.tags[0].value;
        variableSet.executionTime = v.data.executionTime || this.opportunities[0].value;
        if (this.value.findIndex(i => i.variableCode === variableSet.variableCode) === -1) {
          this.value = this.value.concat([variableSet]);
        }
      }
      dialog.close();
    });
    compRef.instance.closeModal.subscribe(re => {
      dialog.close();
    });
    const modalConfig = {
      title: this.localePipe.transform("component.variableSetting.processVariables"),
      width: 660,
      height: 460,
      showButtons: true,
      buttons: compRef.instance.modalFooter
    };
    dialog = this.modalService.show(compRef, modalConfig);
  }

  delete() {
    this.grid.endCellEdit();
    const selected = this.grid.selectedRow;
    if (!selected) {
      this.message.info(this.localePipe.transform("component.variableSetting.notSelectedVar"));
      return;
    }
    this.value = this.value.filter(v => v.variableCode !== selected.data.variableCode);
  }

  clickOK() {
    // 先结束编辑状态
    this.grid.endCellEdit();
    // 等待编辑状态结束后再进行校验
    setTimeout(() => {
      if (this.validator()) {
        const object = { value: this.value, parameters: null };
        this.submitModal.emit(object);
      }
    }, 300);
  }

  clickCancel() {
    this.closeModal.emit();
  }

  validator() {
    for (const item of this.value) {
      if (!item.value) {
        this.message.warning(this.localePipe.transform("component.variableSetting.valueCannotEmpty"));
        return false;
      }
    }
    return true;
  }
  beforeEdit = (param: RowEditParam) => {
    const rowData = param.rowData as any;
    const column = param.column;
    if (column.field === 'valueText') {
      if ((rowData as VariableSetting).tag === this.tags[0].value) {
        param.column.editor = { type: EditorTypes.TEXTBOX, options: {} };
      } else {
        param.column.editor = {
          type: 'input-group',
          options: {
            groupText: '<i class="f-icon f-icon-lookup"></i>',
            editable: false,
            showClear: false,
            clickHandle: (e) => {
              e.formControl.setValue(e.instance.value ? e.instance.value : '');
              this.valueEdit(e);
            },
            clear: (e) => {
              console.log('clear', e);
            }
          }
        };
      }
    }
    return of(true);
  }

  endEdit(data: any) {
    if (data.column.field === 'valueText' && data.rowData.tag === this.tags[0].value) {
      data.rowData.value = data.rowData.valueText;
    }
  }

  valueEdit(e: any) {
    const rowdata: VariableSetting = e.rowData;
    switch (rowdata.tag) {
      case this.tags[1].value:
        this.editExpression(e);
        break;
      case this.tags[2].value:
        this.editComponent(e);
        break;
    }
  }

  private editExpression(e: any) {
    // this.expr.clearContext();
    const data: VariableSetting = e.rowData;
    if (this.uistate.schemas.length > 0) {
      for (const schema of this.uistate.schemas) {
        this.expr.addSchema('Schema', schema);
      }
      if (this.uistate.schemas[0].entityTypes && this.uistate.schemas[0].entityTypes.length > 0) {
        const entityType = this.uistate.schemas[0].entityTypes[0];
        this.expr.addInjectedEntity(entityType.name, 'Schema',
          entityType.name, InjectedType.EntityType);
      }
    }
    let context = new Array();
    context = this.uistate.addContext(context);
    if (this.uistate.subActivityContext.length > 0) {
      for (const variable of this.uistate.subActivityContext) {
        if (!context.some(c => c.key === variable.key)) {
          context.push(variable);
        }
      }
    }
    if (context.length > 0) {
      for (const v of context) {
        this.expr.addContext(v);
      }
    }
    this.expr.setEditPattern(ExprEditMode.Advanced); // 表达式展现模式：默认高级版
    this.expr.addExpressionText(data.value);
    this.expr.buildExpression().then((re: string) => {
      e.formControl.setValue(re);
      e.instance.cd.detectChanges();
      data.value = re;
      e.form.bindingData.value = re;
      e.form.value.value = re;
      //点击确定后，去除表达式里面的新增的上下文变量
      if(context.length > 0) {
        this.expr.exprEntity.contextEntities = this.expr.exprEntity.contextEntities.filter(x => !context.some(y => y.key === x.key));
      }
    });
  }

  private editComponent(e: any) {
    const data: VariableSetting = e.rowData;
    let dialog: any;
    const compFactory = this.resolver.resolveComponentFactory(ComponentEditComponent);
    const compRef = compFactory.create(this.injector);
    compRef.instance.component = data.component;
    compRef.instance.submitEmitter.subscribe((re: any) => {
      e.formControl.setValue(re.Name + '-' + re.MethodCode);
      e.instance.cd.detectChanges();
      data.value = re.Id;
      data.component = re;
      e.form.bindingData.value = re.Id;
      e.form.value.value = re.Id;
      e.form.value.component = re;
      e.form.bindingData.component = re;
      dialog.close();
    });
    compRef.instance.closeEmitter.subscribe(re => {
      dialog.close();
    });
    const modalConfig = {
      title: this.localePipe.transform("component.variableSetting.component"),
      width: 800,
      height: 515,
      showButtons: true,
      buttons: compRef.instance.footer
    };
    dialog = this.modalService.show(compRef, modalConfig);
  }
}

export class VariableSettingConverter implements TypeConverter {
  constructor(
    private localePipe: WfLocalePipe
  ) { }

  convertTo(data: any[]): string {
    if (data && data.length > 0) {
      return this.localePipe.transform("component.variableSetting.set");
    } else {
      return this.localePipe.transform("component.variableSetting.notSet");;
    }
  }
}
